import { useCallback, useEffect, useRef, useState } from 'react'
import { t } from '@lingui/core/macro'
import { SyncAlt, ExpandMore, ExpandLess } from '@mui/icons-material'
import axios from 'axios'
import moment from 'moment'
import { ButtonWithOverlay } from '@components/buttons/buttonWithOverlay'
import { useOverlay } from '@global/hooks/useOverlay'
import { api } from 'api'
import { Integrations } from './integrations'
import * as css from './styles'

interface AirbyteSync {
  job_name: string
  next_fire_time: number
  prev_fire_time: number
  slug: string
  status: string
}

export const AirbyteSyncStatus = {
  FAILURE: 'FAILURE',
  SUBMISSION_QUEUED: 'SUBMISSION QUEUED',
  SUBMISSION_INDEXED: 'SUBMISSION INDEXED',
  PENDING: 'PENDING',
  NORMALIZING: 'NORMALIZING',
  NOT_RUN: 'NOT RUN',
} as const

export type AirbyteSyncStatus = (typeof AirbyteSyncStatus)[keyof typeof AirbyteSyncStatus]

interface IntegrationLastSyncProps {
  datasetId: number
}

interface StateProps {
  integrationAssignmentSyncData: AirbyteSync[]
  hasSuccessfulData: boolean
  hasFailedData: boolean
  showSuccessful: boolean
  showFailed: boolean
}

export const integrationName = (integration: string) => {
  const integrations = {
    'box-push': t`Box`,
    'box-push-general': t`Box`,
    's3-push': t`S3`,
    'azure-blob-push': t`Azure`,
    'sftp-push': t`SFTP`,
    'sftp-pull': t`SFTP`,
    'google-cloud': t`Google Cloud`,
    'facebook-ads': t`Facebook Ads`,
    'google-dcm': t`Google DCM`,
    bitly: t`Bit.ly`,
    'google-dcm-output': t`Google DCM`,
    'email-output': t`Email`,
    'workfront-project-pull-task-trigger': t`Workfront`,
    'facebook-ads-update': t`Facebook Ads`,
    'aws-s3-inbound': t`S3`,
    'qr-code-monkey': t`QR Code Monkey`,
    'adobe-inbound': t`Adobe`,
    'adobe-experience-manager-inbound': t`Adobe Experience Manager`,
    'adobe-experience-manager-outbound': t`Adobe Experience Manager`,
    branch: t`Branch`,
    'google-analytics': t`Google Analytics`,
    'google-ads-inbound': t`Google Ads`,
    'amazon-ads-inbound': t`Amazon Ads`,
    'snapchat-marketing-inbound': t`Snapchat Ads Manager`,
    'pinterest-inbound': t`Pinterest`,
    'facebook-marketing-inbound': t`Meta`,
    'facebook-marketing-outbound': t`Meta`,
    'google-cm360-inbound': t`Google CM360`,
    'google-cm360-outbound': t`Google CM360`,
    'tiktok-inbound': t`TikTok`,
  }

  return integrations[integration]
}

const LastSyncButtonLabel = ({ lastSyncCopy }) => {
  const [label, date] = lastSyncCopy.split(`:`)
  return (
    <>
      <span className="last-sync-label">{label}:</span>
      <span className="last-sync-date">{date}</span>
    </>
  )
}

export const IntegrationLastSync = ({ datasetId }: IntegrationLastSyncProps) => {
  const [state, setState] = useState<StateProps>({
    integrationAssignmentSyncData: [],
    hasSuccessfulData: false,
    hasFailedData: false,
    showSuccessful: true,
    showFailed: true,
  })
  const {
    integrationAssignmentSyncData,
    hasSuccessfulData,
    hasFailedData,
    showSuccessful,
    showFailed,
  } = state

  useEffect(() => {
    const requestSource = axios.CancelToken.source()
    const axiosOptions = {
      cancelToken: requestSource.token,
    }
    const cancelMessage = t`IntegrationLastSync useEffect cleanup function cancelled request.`

    async function getAirByteConnections() {
      try {
        const { data } = await api.post<{ datasetId: number }, { data: AirbyteSync[] }>(
          '/api/v1/integration-assignments/get-airbyte-connection-ids-by-dataset-id',
          { datasetId },
          axiosOptions,
        )
        setState(prevState => ({
          ...prevState,
          integrationAssignmentSyncData: data,
        }))
      } catch (err) {
        if (err instanceof Error && err.message === cancelMessage) return null // swallow cancelled request.
        console.error('getAirByteConnections API failed:', err)
      }
    }

    void getAirByteConnections()

    return () => {
      requestSource.cancel(cancelMessage)
    }
  }, [])

  const lastSyncCopy = integration => {
    const date = integration?.prev_fire_time
      ? moment(new Date(parseInt(integration?.prev_fire_time, 10))).fromNow()
      : 'unknown'

    switch (integration?.status) {
      case AirbyteSyncStatus.PENDING:
      case AirbyteSyncStatus.NORMALIZING:
        return t`Sync in progress`
      case AirbyteSyncStatus.FAILURE:
        return t`Failed sync: ${date}`
      default:
        return t`Last sync: ${date}`
    }
  }

  const buttonRef = useRef(null)
  const { anchorEl, onClick, onClose, open, id } = useOverlay()

  const handleClose = (event: any, reason: string) => {
    onClose(event, reason)
  }

  const filteredData = integrationAssignmentSyncData.filter(integration => {
    if (!showSuccessful && integration.status !== AirbyteSyncStatus.FAILURE) return false
    if (!showFailed && integration.status === AirbyteSyncStatus.FAILURE) return false
    return true
  })

  useEffect(() => {
    const hasFailedItems = integrationAssignmentSyncData.some(
      integration => integration.status === AirbyteSyncStatus.FAILURE,
    )
    const hasSuccessfulItems = integrationAssignmentSyncData.some(
      integration => integration.status !== AirbyteSyncStatus.FAILURE,
    )

    setState(prevState => ({
      ...prevState,
      hasSuccessfulData: hasSuccessfulItems,
      hasFailedData: hasFailedItems,
      showSuccessful: hasSuccessfulItems,
      showFailed: hasFailedItems,
    }))
  }, [integrationAssignmentSyncData])

  const hasOnlySuccessfulData = hasSuccessfulData && !hasFailedData
  const hasOnlyFailedData = hasFailedData && !hasSuccessfulData

  const syncData = integrationAssignmentSyncData[0]
  const isSingleIntegration = integrationAssignmentSyncData.length === 1
  const hasError = isSingleIntegration && syncData?.status === AirbyteSyncStatus.FAILURE
  const singleIntegrationName =
    isSingleIntegration && syncData ? integrationName(syncData.slug) : ''

  const toggleShowSuccessful = useCallback(() => {
    setState(prevState => ({ ...prevState, showSuccessful: !prevState.showSuccessful }))
  }, [])

  const toggleShowFailed = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showFailed: !prevState.showFailed,
    }))
  }, [])

  const integrationsSyncedCount = filteredData?.length

  return syncData ? (
    <ButtonWithOverlay>
      <ButtonWithOverlay.Button
        popoverId="import-list"
        ref={buttonRef}
        onClick={onClick}
        disabled={isSingleIntegration}
        className={hasError ? `-has-issues` : `-has-no-issues`}
        extendStyles={(isSingleIntegration && css.Button) || undefined}
      >
        {isSingleIntegration ? (
          <>
            <img
              src={`https://claravine-public-prod.s3-us-west-2.amazonaws.com/images/${syncData.slug}.png`}
              height="24"
              style={{ width: 24, height: 24 }}
              alt={singleIntegrationName || ''}
            />
            <strong>{singleIntegrationName} —</strong>
            <LastSyncButtonLabel lastSyncCopy={lastSyncCopy(syncData)} />
          </>
        ) : (
          <>
            <SyncAlt />
            {t`Integrations synced (${integrationsSyncedCount})`}
            {open ? <ExpandLess /> : <ExpandMore />}
          </>
        )}
      </ButtonWithOverlay.Button>
      <ButtonWithOverlay.Overlay
        popoverProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          open,
          onClose: handleClose,
          anchorEl,
          id,
        }}
        sx={{
          '& .MuiPaper-root': {
            marginTop: '8px',
            padding: 0,
          },
        }}
      >
        <Integrations>
          <Integrations.List>
            {filteredData.map((integration, index) => (
              <Integrations.ListItem
                key={integration?.job_name}
                index={index}
                integration={integration}
                lastSyncCopy={lastSyncCopy}
              />
            ))}
          </Integrations.List>
          <Integrations.Footer
            showSuccessful={showSuccessful}
            setShowSuccessful={toggleShowSuccessful}
            disableShowSuccessfulCheckbox={!hasSuccessfulData || hasOnlySuccessfulData}
            showFailed={showFailed}
            setShowFailed={toggleShowFailed}
            disableShowFailedCheckbox={!hasFailedData || hasOnlyFailedData}
          />
        </Integrations>
      </ButtonWithOverlay.Overlay>
    </ButtonWithOverlay>
  ) : null
}
